import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import {
  AmountInput,
  Button,
  CurrenciesUSD,
  Dropdown,
  Input,
  Modal,
  ModalSize,
  SegmentedControl,
  SegmentedControlSize,
} from '@column/column-ui-kit';

import { PopoverFilterTransferDateData } from '~/components';
import { DateInput } from '~/components/DateInput';
import { Flex } from '~/styles';
import { parseDateTime } from '~/util/dateFilterHelpers';

import { PopoverFilterBalanceData } from './PopoverFilterBalance';

type BankAccountType = '' | 'CHECKING' | 'OVERDRAFT_RESERVE' | 'PROGRAM_RESERVE' | 'UNKNOWN';
type OverdraftableSelection = '' | 'true' | 'false';

const TYPE_OPTIONS: { label: string; value: BankAccountType }[] = [
  { label: 'Any', value: '' },
  { label: 'Checking', value: 'CHECKING' },
  { label: 'Overdraft Reserve', value: 'OVERDRAFT_RESERVE' },
  { label: 'Program Reserve', value: 'PROGRAM_RESERVE' },
  { label: 'Unknown', value: 'UNKNOWN' },
];

const OVERDRAFTABLE_OPTIONS: { label: string; value: OverdraftableSelection }[] = [
  { label: 'Any', value: '' },
  { label: 'Overdraftable', value: 'true' },
  { label: 'Not overdraftable', value: 'false' },
];

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 var(--page-margin) 24px;
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

  &:first-child {
    padding-top: 8px;
  }
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

interface FormValues {
  type: BankAccountType;
  overdraftable: OverdraftableSelection;
  balanceGte?: number;
  balanceLte?: number;
  query: string;
  uiStartDate: { date?: string };
  uiEndDate: { date?: string };
}

interface BankAccountFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  showBalance: boolean;
  initialType: BankAccountType;
  initialOverdraftable: '' | 'true' | 'false';
  initialBalance: PopoverFilterBalanceData;
  initialQuery: string;
  initialDate: Partial<PopoverFilterTransferDateData>;
  convertDateRange: (params: {
    startDateString?: string;
    endDateString?: string;
  }) => Partial<PopoverFilterTransferDateData>;
  onApply: (values: {
    type: BankAccountType;
    overdraftable: '' | 'true' | 'false';
    balance: PopoverFilterBalanceData;
    query: string;
    date: Partial<PopoverFilterTransferDateData>;
  }) => void;
}

export const BankAccountFiltersModal: React.FC<BankAccountFiltersModalProps> = ({
  isOpen,
  onClose,
  showBalance,
  initialType,
  initialOverdraftable,
  initialBalance,
  initialQuery,
  initialDate,
  convertDateRange,
  onApply,
}) => {
  const methods = useForm<FormValues>({
    defaultValues: {
      type: initialType,
      overdraftable: initialOverdraftable,
      balanceGte: initialBalance.balanceGte,
      balanceLte: initialBalance.balanceLte,
      query: initialQuery,
      uiStartDate: initialDate.createdAtGte ? parseDateTime(initialDate.createdAtGte) : {},
      uiEndDate: initialDate.createdAtLt ? parseDateTime(initialDate.createdAtLt, true) : {},
    },
  });
  const { watch, setValue, reset, getValues } = methods;

  useEffect(() => {
    if (!isOpen) return;
    reset({
      type: initialType,
      overdraftable: initialOverdraftable,
      balanceGte: initialBalance.balanceGte,
      balanceLte: initialBalance.balanceLte,
      query: initialQuery,
      uiStartDate: initialDate.createdAtGte ? parseDateTime(initialDate.createdAtGte) : {},
      uiEndDate: initialDate.createdAtLt ? parseDateTime(initialDate.createdAtLt, true) : {},
    });
  }, [isOpen, initialType, initialOverdraftable, initialBalance, initialQuery, initialDate, reset]);

  const handleApply = () => {
    const fv = getValues();
    const datePayload = convertDateRange({
      startDateString: fv.uiStartDate?.date,
      endDateString: fv.uiEndDate?.date,
    });
    onApply({
      type: fv.type,
      overdraftable: fv.overdraftable,
      balance: {
        balanceGte: fv.balanceGte,
        balanceLte: fv.balanceLte,
      },
      query: fv.query,
      date: {
        createdAtGte: datePayload.createdAtGte,
        createdAtLt: datePayload.createdAtLt,
      },
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.Medium}>
      <Modal.Header title="Filters" />
      <FormProvider {...methods}>
        <Flex flexDirection="column" gap={16}>
          <Section>
            <Label>{showBalance ? 'Search' : 'Description'}</Label>
            <Input
              size="small"
              value={watch('query')}
              placeholder={showBalance ? 'Search accounts...' : 'Search by description...'}
              onChange={(value: string) => setValue('query', value)}
            />
          </Section>

          <Section>
            <Label>Type</Label>
            <Dropdown
              size="small"
              fullWidth
              active={watch('type')}
              options={TYPE_OPTIONS}
              label={TYPE_OPTIONS.find((o) => o.value === watch('type'))?.label ?? 'Any'}
              onChange={(value: BankAccountType) => setValue('type', value)}
            />
          </Section>

          <Section>
            <Label>Overdraftable</Label>
            <SegmentedControl
              size={SegmentedControlSize.Small}
              options={OVERDRAFTABLE_OPTIONS}
              fullWidth
              active={watch('overdraftable')}
              onOptionChange={(value) => setValue('overdraftable', (value as OverdraftableSelection) ?? '')}
            />
          </Section>

          {showBalance && (
            <Section>
              <Label>Balance</Label>
              <Flex gap={12}>
                <Flex flexDirection="column" gap={8} auto>
                  <Label>Minimum</Label>
                  <AmountInput
                    value={watch('balanceGte')}
                    onChange={(value) => setValue('balanceGte', value && value > 0 ? value : undefined)}
                    fixedCurrencyCode="USD"
                    currencyList={CurrenciesUSD}
                  />
                </Flex>
                <Flex flexDirection="column" gap={8} auto>
                  <Label>Maximum</Label>
                  <AmountInput
                    value={watch('balanceLte')}
                    onChange={(value) => setValue('balanceLte', value && value > 0 ? value : undefined)}
                    fixedCurrencyCode="USD"
                    currencyList={CurrenciesUSD}
                  />
                </Flex>
              </Flex>
            </Section>
          )}

          <Section>
            <Label>Created</Label>
            <Flex gap={12}>
              <Flex flexDirection="column" gap={8} auto>
                <Label>Start</Label>
                <DateInput
                  size="small"
                  value={watch('uiStartDate')?.date}
                  onChange={(value) => setValue('uiStartDate', { ...(watch('uiStartDate') ?? {}), date: value })}
                  renderInside
                />
              </Flex>
              <Flex flexDirection="column" gap={8} auto>
                <Label>End</Label>
                <DateInput
                  size="small"
                  value={watch('uiEndDate')?.date}
                  onChange={(value) => setValue('uiEndDate', { ...(watch('uiEndDate') ?? {}), date: value })}
                  renderInside
                />
              </Flex>
            </Flex>
          </Section>

          <Modal.Footer>
            <Button size="small" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button size="small" onClick={handleApply}>
              Apply
            </Button>
          </Modal.Footer>
        </Flex>
      </FormProvider>
    </Modal>
  );
};
